import { useState, type ReactNode } from 'react';
import { Disclosure } from './Disclosure';
import { StepProgress, type OperationStep } from './StepProgress';
import { EmptyState } from './EmptyState';
import { cn } from './cn';

export interface LiveOperationStep extends OperationStep {
  details: ReactNode;
  /** Известная доля текущего шага, 0–100; без неё процент не выдумывается. */
  progress?: number;
}
interface LiveStepListProps {
  steps: readonly LiveOperationStep[];
  initiator?: 'system' | 'ai';
  variant?: 'cards' | 'compact';
  paused?: boolean;
  expanded?: readonly string[];
  defaultExpanded?: readonly string[];
  onExpandedChange?: (ids: string[]) => void;
  summary?: ReactNode;
  headingLevel?: 2 | 3 | 4 | 5 | 6;
  className?: string;
}
const STATUS = { done: 'Сделано', active: 'Идёт', pending: 'Ждёт', failed: 'Сбой' } as const;

export function LiveStepList({
  steps,
  initiator = 'system',
  variant = 'cards',
  paused = false,
  expanded,
  defaultExpanded = [],
  onExpandedChange,
  summary,
  headingLevel = 3,
  className,
}: LiveStepListProps) {
  const [localExpanded, setLocalExpanded] = useState(defaultExpanded);
  const opened = expanded ?? localExpanded;
  const failed = steps.findIndex((step) => step.status === 'failed');
  const active = steps.findIndex((step) => step.status === 'active');
  const current =
    failed >= 0
      ? failed + 1
      : active >= 0
        ? active + 1
        : steps.every((step) => step.status === 'done')
          ? steps.length
          : steps.findIndex((step) => step.status === 'pending') + 1;
  const announcement =
    failed >= 0
      ? 'Операция остановлена: ' + steps[failed].label
      : active >= 0
        ? (paused ? 'Пауза: ' : 'Выполняется: ') + steps[active].label
        : steps.length && steps.every((step) => step.status === 'done')
          ? 'Операция завершена'
          : 'Ожидание следующего шага';
  if (!steps.length)
    return <EmptyState title="Шагов пока нет" description="Они появятся, когда начнётся операция." />;
  return (
    <div
      data-slot="live-step-list"
      data-initiator={initiator}
      data-variant={variant}
      className={cn(
        'min-w-0',
        variant === 'compact' && 'overflow-hidden rounded-lg border border-line bg-card',
        className,
      )}
    >
      <p role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </p>
      <StepProgress
        steps={steps}
        currentStep={current}
        statusText={
          failed < 0 && active < 0 && steps.some((step) => step.status === 'pending') ? 'Ожидание' : undefined
        }
        paused={paused}
        tone={initiator === 'ai' ? 'ai' : 'brand'}
        renderStep={(step, row) => {
          const item = steps.find((candidate) => candidate.id === step.id)!;
          const progress =
            typeof item.progress === 'number' && Number.isFinite(item.progress)
              ? Math.min(100, Math.max(0, item.progress))
              : undefined;
          return (
            <div
              data-live-step={step.id}
              data-status={step.status}
              className={
                variant === 'compact' ? 'relative' : 'overflow-hidden rounded-lg border border-line bg-card'
              }
            >
              {variant === 'compact' && steps.length > 1 && (
                <span
                  data-step-connector=""
                  aria-hidden
                  className={cn(
                    'pointer-events-none absolute left-6 border-l border-line-strong',
                    step.id === steps.at(-1)?.id
                      ? 'top-0 h-4'
                      : step.id === steps[0].id
                        ? 'top-4 bottom-0'
                        : 'inset-y-0',
                  )}
                />
              )}
              <Disclosure
                title={
                  <span className={cn('flex min-w-0 flex-col', variant === 'compact' ? 'text-xs' : 'gap-1')}>
                    <span className="flex min-w-0 items-center gap-2">{row}</span>
                    <span
                      className={
                        variant === 'compact' ? 'sr-only' : 'ml-6 text-2xs font-normal text-fg-muted'
                      }
                    >
                      {paused && step.status === 'active' ? 'Пауза' : STATUS[step.status]}
                      {step.status === 'active' && progress === undefined && ' · доля неизвестна'}
                    </span>
                  </span>
                }
                variant="row"
                size={variant === 'compact' ? 'sm' : 'md'}
                contentSpacing={variant === 'compact' ? 'indent' : 'normal'}
                headingLevel={headingLevel}
                region={false}
                open={opened.includes(step.id)}
                onOpenChange={(open) => {
                  const next = open ? [...opened, step.id] : opened.filter((id) => id !== step.id);
                  if (expanded === undefined) setLocalExpanded(next);
                  onExpandedChange?.(next);
                }}
              >
                {item.details}
              </Disclosure>
              <div
                className={
                  variant === 'compact' ? 'mx-4 ml-10 h-1 overflow-hidden' : 'h-1 overflow-hidden bg-muted'
                }
              >
                {step.status === 'active' && (
                  <div
                    role="progressbar"
                    aria-label={'Ход шага «' + step.label + '»'}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={progress}
                    aria-valuetext={progress === undefined ? 'Доля выполнения пока неизвестна' : undefined}
                    className={cn(
                      'h-full transition-interactive motion-reduce:transition-none',
                      progress === undefined
                        ? 'border-t border-dashed border-line-strong'
                        : initiator === 'ai'
                          ? 'bg-ai-fg'
                          : 'bg-brand-fg',
                    )}
                    style={progress === undefined ? undefined : { width: progress + '%' }}
                  />
                )}
              </div>
            </div>
          );
        }}
      />
      {summary && (
        <section
          data-slot="step-summary"
          aria-label="Итог операции"
          className={
            variant === 'compact'
              ? 'border-t border-line bg-muted px-4 py-3 text-sm text-fg'
              : 'mt-4 rounded-lg border border-line bg-muted p-4 text-sm text-fg'
          }
        >
          {summary}
        </section>
      )}
    </div>
  );
}
